// Keyboard behaviour for modal dialogs (role="dialog" aria-modal="true"):
// focus moves into the dialog when it opens, Tab and Shift+Tab stay inside it,
// Escape closes it, and focus returns to whatever opened it. Without this an
// aria-modal dialog left focus on the button behind the backdrop, so keyboard
// and screen-reader users were working in a page they could not see.

import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Focusable elements inside `root`, in DOM (tab) order. */
export function focusableIn(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => !el.hidden && !el.closest('[hidden], [inert]'));
}

/**
 * Keep Tab inside `root`: from the last focusable wrap to the first, and from
 * the first (or from outside the dialog) Shift+Tab to the last. Returns true
 * when it moved focus (the caller's event was handled).
 */
export function trapTab(root: HTMLElement, event: Pick<KeyboardEvent, 'key' | 'shiftKey' | 'preventDefault'>): boolean {
  if (event.key !== 'Tab') return false;
  const items = focusableIn(root);
  if (items.length === 0) {
    event.preventDefault();
    return true;
  }
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = root.ownerDocument.activeElement;
  const inside = active instanceof Node && root.contains(active);
  if (event.shiftKey && (active === first || !inside)) {
    event.preventDefault();
    last.focus();
    return true;
  }
  if (!event.shiftKey && (active === last || !inside)) {
    event.preventDefault();
    first.focus();
    return true;
  }
  return false;
}

/**
 * Wire a modal dialog's keyboard behaviour. `onClose` runs on Escape. Pass
 * `open` when the dialog is rendered conditionally by the calling component,
 * and `canClose: false` while it must stay open (e.g. a save in flight).
 */
export function useDialogFocus(
  ref: RefObject<HTMLElement>,
  onClose: () => void,
  { open = true, canClose = true }: { open?: boolean; canClose?: boolean } = {},
): void {
  // Latest values without re-running the mount effect (which would steal
  // focus back to the first field on every render).
  const onCloseRef = useRef(onClose);
  const canCloseRef = useRef(canClose);
  onCloseRef.current = onClose;
  canCloseRef.current = canClose;

  useEffect(() => {
    const root = ref.current;
    if (!open || !root) return undefined;
    const opener = root.ownerDocument.activeElement as HTMLElement | null;
    if (!root.contains(opener)) (focusableIn(root)[0] ?? root).focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!canCloseRef.current) return;
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      trapTab(root, e);
    };
    root.addEventListener('keydown', onKeyDown);
    return () => {
      root.removeEventListener('keydown', onKeyDown);
      // Back to the control that opened the dialog, if it is still there.
      if (opener?.isConnected) opener.focus();
    };
  }, [ref, open]);
}
